import React, { useEffect, useState } from 'react';
import WeatherIcon from 'components/other/WeatherIcon'; // Assuming WeatherIcon is a custom component
import Navigation from 'components/other/Navigation'; // Assuming Navigation is a custom component
import DailyThreshold from 'components/other/DailyThreshold';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFire,
  faTimes,
  faSave,
  faCalendarDay,
  faEdit,
  faTrashAlt,
  faPlus,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { getAlerts } from 'services/alertsService';
import {
  listAlertConfigurations,
  createAlertConfiguration,
  updateAlertConfiguration,
  deleteAlertConfiguration,
} from 'services/alertConfigurationsService';
import { listEmails, createEmail, deleteEmail } from 'services/emailsService';
import {
  AlertData,
  AlertConfigData,
  AlertConfigRecord,
  EmailData,
} from 'types';

export function AlertsScreen() {
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [alertsConfig, setAlertsConfig] = useState<AlertConfigRecord[]>([]);
  const [emailList, setEmailList] = useState<EmailData[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [editingConfig, setEditingConfig] = useState<AlertConfigRecord | null>(
    null
  );
  const [isAddingConfig, setIsAddingConfig] = useState<boolean>(false);

  const rateLabels = {
    temperature_forecasted: {
      label: 'Temperature Forecasted',
      unit: '°F',
      days: true,
      operator: 'is less than',
    },
    gas_daily_usage: {
      label: 'Gas Daily Usage',
      unit: 'MCF',
      days: true,
      operator: 'is greater than',
    },
    gas_usage_rate: {
      label: 'Instantaneous Gas Usage Rate',
      unit: 'MCF/hr',
      days: false,
      operator: 'is greater than',
    },
    base_rate: {
      label: 'Base MDQ Rate',
      unit: 'MCF',
      days: true,
      operator: 'is',
    },
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      const alertsData = await getAlerts(
        'temperature_forecasted,gas_daily_usage,gas_usage_rate,gas_consumption_rate'
      );
      setAlerts(alertsData.slice(-10));
    };

    const fetchAlertConfigurations = async () => {
      const alertConfigsData = await listAlertConfigurations();
      setAlertsConfig(alertConfigsData);
    };

    const fetchEmails = async () => {
      const emailsData = await listEmails();
      setEmailList(emailsData);
    };

    fetchAlerts();
    fetchAlertConfigurations();
    fetchEmails();
  }, []);

  const handleAddEmail = async () => {
    if (newEmail) {
      await createEmail({ email: newEmail });
      const updatedEmails = await listEmails();
      setEmailList(updatedEmails);
      setNewEmail('');
    }
  };

  const handleDeleteEmail = async (id: string) => {
    await deleteEmail(id);
    const updatedEmails = await listEmails();
    setEmailList(updatedEmails);
  };

  const handleAddConfig = () => {
    const newConfig: AlertConfigRecord = {
      id: '',
      data: {
        days: 0,
        threshold: 0,
        type: 'temperature_forecasted',
      },
    };
    setEditingConfig(newConfig);
    setIsAddingConfig(true);
  };

  const handleEditConfig = (config: AlertConfigRecord) => {
    setEditingConfig(config);
  };

  const handleSaveConfig = async () => {
    if (editingConfig) {
      if (isAddingConfig) {
        await createAlertConfiguration(editingConfig.data);
        setIsAddingConfig(false);
      } else {
        await updateAlertConfiguration(editingConfig.id, editingConfig.data);
      }
      const updatedConfigs = await listAlertConfigurations();
      setAlertsConfig(updatedConfigs);
      setEditingConfig(null);
    }
  };

  const handleDeleteConfig = async (id: string) => {
    await deleteAlertConfiguration(id);
    const updatedConfigs = await listAlertConfigurations();
    setAlertsConfig(updatedConfigs);
  };

  const handleConfigTypeChange = (value: string) => {
    if (editingConfig) {
      setEditingConfig({
        ...editingConfig,
        data: {
          ...editingConfig.data,
          type: value,
        },
      });
    }
  };

  const handleConfigChange = (field: string, value: number) => {
    if (editingConfig) {
      setEditingConfig({
        ...editingConfig,
        data: {
          ...editingConfig.data,
          [field]: value,
        },
      });
    }
  };

  const hasBaseRateConfig = () =>
    alertsConfig.some((config) => config.data.type === 'base_rate');

  const alertConfigRow = (alert: AlertConfigRecord, index: number) => (
    <div key={index} className="flex items-center space-x-2 py-2">
      <FontAwesomeIcon icon={faCalendarDay} className="text-xl" />{' '}
      {/* Assuming icon based on type */}
      {editingConfig && editingConfig.id === alert.id ? (
        <>
          <span>The</span>
          {editingConfig.data.type === 'base_rate' ? (
            <span>{rateLabels[editingConfig.data.type as keyof typeof rateLabels].label}</span>
          ) : (
            <select
              value={editingConfig.data.type}
              onChange={(e) => handleConfigTypeChange(e.target.value)}
              className="bg-lightblue w-52 rounded px-2 py-1 text-black"
            >
              <option value="temperature_forecasted">
                {rateLabels.temperature_forecasted.label}
              </option>
              <option value="gas_daily_usage">
                {rateLabels.gas_daily_usage.label}
              </option>
              <option value="gas_usage_rate">
                {rateLabels.gas_usage_rate.label}
              </option>
            </select>
          )}
          {editingConfig.data.type !== 'base_rate' ? (
            <span>{`${rateLabels[editingConfig.data.type as keyof typeof rateLabels].operator}`}</span>
          ) : (
            <span>is</span>
          )}
          <input
            type="number"
            value={editingConfig.data.threshold}
            onChange={(e) =>
              handleConfigChange('threshold', parseFloat(e.target.value))
            }
            className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
            placeholder="___"
          />
          <span>
            {rateLabels[editingConfig.data.type as keyof typeof rateLabels].unit}
          </span>{' '}
          {editingConfig.data.days !== undefined &&
            rateLabels[editingConfig.data.type as keyof typeof rateLabels].days && (
              <>
                {editingConfig.data.type === 'base_rate' ? (
                  <span>and resets after</span>
                ) : (
                  <span>for at least</span>
                )}
                <input
                  type="number"
                  value={editingConfig.data.days}
                  onChange={(e) =>
                    handleConfigChange('days', parseInt(e.target.value))
                  }
                  className="bg-lightblue w-12 rounded px-2 py-1 text-center text-black"
                  placeholder="___"
                />
                <span>days</span>
              </>
            )}
          <button onClick={handleSaveConfig} className="ml-2 text-green-500">
            <FontAwesomeIcon icon={faSave} />
          </button>
          <button
            onClick={() => setEditingConfig(null)}
            className="ml-2 text-red-500"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </>
      ) : (
        <>
          <span>{`The ${rateLabels[alert.data.type as keyof typeof rateLabels].label}`}</span>
          {alert.data.type !== 'base_rate' ? (
            <span>{`${rateLabels[alert.data.type as keyof typeof rateLabels].operator}`}</span>
          ) : (
            <span>is</span>
          )}
          <input
            type="text"
            value={alert.data.threshold}
            readOnly
            className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
            placeholder="___"
          />
          <span>
            {rateLabels[alert.data.type as keyof typeof rateLabels].unit}
          </span>{' '}
          {/* Replace 'units' with appropriate unit based on type */}
          {alert.data.days !== undefined &&
            rateLabels[alert.data.type as keyof typeof rateLabels].days && (
              <>
                {alert.data.type === 'base_rate' ? (
                  <span>and resets after</span>
                ) : (
                  <span>for at least</span>
                )}
                <input
                  type="text"
                  value={alert.data.days}
                  readOnly
                  className="bg-lightblue w-12 rounded px-2 py-1 text-center text-black"
                  placeholder="___"
                />
                <span>days</span>
              </>
            )}
          <FontAwesomeIcon
            icon={faEdit}
            className="ml-auto cursor-pointer text-xl"
            onClick={() => handleEditConfig(alert)}
          />
          <FontAwesomeIcon
            icon={faTrashAlt}
            className="cursor-pointer text-xl"
            onClick={() => handleDeleteConfig(alert.id)}
          />
        </>
      )}
    </div>
  );

  return (
    <div className="screen new-screen overflow-hidden bg-background text-white">
      <div className="flex w-full justify-center">
        <div className="flex w-[90%] flex-col">
          {/* Weather and Tabs */}
          <div className="container mx-auto flex items-end justify-between space-x-2 bg-light-blue-500 px-20 pt-4">
            <div className="flex space-x-4">
              <button
                className={`rounded-t-lg px-4 py-2 text-white ${activeTab === 'alerts' ? 'bg-background' : 'bg-light-blue-500'}`}
                onClick={() => setActiveTab('alerts')}
              >
                Alerts
              </button>
              <button
                className={`rounded-t-lg px-4 py-2 text-white ${activeTab === 'config' ? 'bg-background' : 'bg-light-blue-500'}`}
                onClick={() => setActiveTab('config')}
              >
                Configuration
              </button>
              <button
                className={`rounded-t-lg px-4 py-2 text-white ${activeTab === 'emails' ? 'bg-background' : 'bg-light-blue-500'}`}
                onClick={() => setActiveTab('emails')}
              >
                E-MAIL
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <WeatherIcon />
            </div>
          </div>

          {activeTab === 'alerts' && (
            <div className="mt-6 grow px-20">
              <div className="flex flex-row-reverse items-center justify-between text-2xl">
                <DailyThreshold />
              </div>
              <div className="bg-darkblue mt-4 rounded-lg p-4">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b border-gray-700 py-2"
                  >
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faFire} className="text-xl" />
                      <span>{new Date(alert.time).toLocaleDateString()}</span>
                    </div>
                    <span className="mx-4 flex-1">{alert.message}</span>
                    <span>{new Date(alert.time).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'config' && (
            <div className="mt-6 grow px-20">
              <div className="mb-4 flex flex-row-reverse items-center justify-between text-2xl">
                <DailyThreshold />
              </div>
              <div className="bg-darkblue rounded-lg p-4">
                <div className="mb-2 text-lg">BASE MDQ</div>
                {alertsConfig
                  .filter((ac) => ac.data.type === 'base_rate')
                  .map((alert, index) => alertConfigRow(alert, index))}
                <div className="mb-2 mt-2 text-lg">TRIGGER AN ALERT IF</div>
                {alertsConfig
                  .filter((ac) => ac.data.type !== 'base_rate')
                  .map((alert, index) => alertConfigRow(alert, index))}
                {isAddingConfig && (
                  <div className="flex items-center space-x-2 py-2">
                    <span>The</span>
                    <select
                      value={
                        editingConfig?.data.type || 'temperature_forecasted'
                      }
                      onChange={(e) => handleConfigTypeChange(e.target.value)}
                      className="bg-lightblue w-52 rounded px-2 py-1 text-black"
                    >
                      <option value="temperature_forecasted">
                        {rateLabels.temperature_forecasted.label}
                      </option>
                      <option value="gas_daily_usage">
                        {rateLabels.gas_daily_usage.label}
                      </option>
                      <option value="gas_usage_rate">
                        {rateLabels.gas_usage_rate.label}
                      </option>
                      {!hasBaseRateConfig() && (
                        <option value="base_rate">
                          {rateLabels.base_rate.label}
                        </option>
                      )}
                    </select>
                    <span>{`${rateLabels[editingConfig?.data.type as keyof typeof rateLabels].operator}`}</span>
                    <input
                      type="number"
                      value={editingConfig?.data.threshold || ''}
                      onChange={(e) =>
                        handleConfigChange(
                          'threshold',
                          parseFloat(e.target.value)
                        )
                      }
                      className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                      placeholder="___"
                    />
                    <span>{rateLabels[editingConfig?.data.type as keyof typeof rateLabels].unit}</span>
                    {rateLabels[editingConfig?.data.type as keyof typeof rateLabels].days && (
                      <>
                      <span>for at least</span>
                      <input
                        type="number"
                        value={editingConfig?.data.days || ''}
                        onChange={(e) =>
                          handleConfigChange('days', parseInt(e.target.value))
                        }
                        className="bg-lightblue w-12 rounded px-2 py-1 text-center text-black"
                        placeholder="___"
                      />
                      <span>days</span>
                      </>
                    )}
                    <button
                      onClick={handleSaveConfig}
                      className="ml-2 text-green-500"
                    >
                      <FontAwesomeIcon icon={faSave} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingConfig(null);
                        setIsAddingConfig(false);
                      }}
                      className="ml-2 text-red-500"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                )}
                <div className="flex items-center space-x-2 py-2">
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="cursor-pointer text-xl"
                    onClick={handleAddConfig}
                  />
                  <span>Add new alert</span>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'emails' && (
            <div className="mt-6 grow px-20">
              <div className="mb-4 flex flex-row-reverse items-center justify-between text-2xl">
                <DailyThreshold />
              </div>
              <div className="bg-darkblue rounded-lg p-4">
                <div className="mb-2 text-lg">
                  ALERTS WILL BE SENT TO THE FOLLOWING E-MAILS:
                </div>
                {emailList.map((email: EmailData, index: number) => (
                  <div key={index} className="flex items-center space-x-2 py-2">
                    <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                    <span>{email.data.email}</span>
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      className="ml-auto cursor-pointer text-xl"
                      onClick={() => handleDeleteEmail(email.id)}
                    />
                  </div>
                ))}
                <div className="flex items-center space-x-2 py-2">
                  <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                  <input
                    type="text"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="bg-lightblue flex-1 rounded px-2 py-1 text-black"
                    placeholder="mailaddress@domain.com"
                  />
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="cursor-pointer text-xl"
                    onClick={handleAddEmail}
                  />
                </div>
              </div>
            </div>
          )}
          <Navigation />
        </div>
      </div>
    </div>
  );
}
